import { FormControlLabel, FormControlLabelText } from '@/components/ui/form-control';
import { Text } from '@/components/ui/text';
import type { SurveyElement } from '../../model/surveySchema';

interface SurveyQuestionLabelProps {
  element: SurveyElement;
  /**
   * Suppresses the visual `*` marker. Used for polls, where the page-level
   * "* Required" legend is also hidden — an asterisk with no legend to
   * explain it would read as a stray character rather than a required-field
   * indicator. The screen-reader `, required` suffix each question passes to
   * its own control is unaffected: that signal has no legend to lose.
   */
  hideRequiredMarker?: boolean;
}

/**
 * A question's title, with the required marker flowing after the last word.
 *
 * The marker is rendered inside the label's own `Text` rather than left to
 * Gluestack's `isRequired` asterisk, which is a sibling node: in a row layout a
 * title long enough to wrap fills the line, pushing that sibling out to the far
 * right margin where it reads as a stray character rather than as part of the
 * question. Short titles kept it inline, so one screen showed two different
 * treatments.
 *
 * The trade this makes is that `isRequired` no longer goes on the `FormControl`
 * — it is what renders the sibling — so each question passes `aria-required` to
 * its own control instead. Keep that pairing: dropping one without the other
 * either brings the stray asterisk back or tells assistive tech the question is
 * optional.
 */
export function SurveyQuestionLabel({ element, hideRequiredMarker }: SurveyQuestionLabelProps) {
  return (
    <FormControlLabel>
      <FormControlLabelText>
        {element.title}
        {element.isRequired && !hideRequiredMarker ? (
          <Text className="font-medium text-typography-900"> *</Text>
        ) : null}
      </FormControlLabelText>
    </FormControlLabel>
  );
}
